import React, {useEffect, useState} from "react";
import { connect } from 'react-redux';
import * as Yup from "yup";
import {hasPermission, parseURL} from "../../sc-utilities";
import History from "../../../History";
import {Dialog, withStyles} from "@material-ui/core";
import AppLogoLoading from "../../sc-logo";
import LabelTemplateForm, {
    Data,
    Format,
    formats,
    Height,
    ProductType,
    System,
    Width,
    Default, 
    Name,
    Purpose,
} from "./LabelTemplateForm";
import * as labelTemplateActions from "./store/labelTemplateActions";

const styles = (theme) => ({
    loadingScreen: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
});

const LabelTemplateDetails = props => {
    const { classes, activeCustomerID, translations, permissions, fetchLabelTemplate, createLabelTemplate, editLabelTemplate } = props;

    const [labelTemplate, setLabelTemplate] = useState(null);
    const [loading, setLoading] = useState(false);

    const searchParams = parseURL(window.location.href);
    const isEdit = window.location.href.includes("edit");

    useEffect(() => {
        const getLabelTemplate = async () => {
            setLoading(true);
            const result = await fetchLabelTemplate({LabelTemplateID: searchParams?.LabelTemplateID, ActiveCustomerID: activeCustomerID});
            if (result) {
                setLabelTemplate(result);
            }
            setLoading(false);

        }
        isEdit && getLabelTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const hasCreateSystemLabelTemplatePermission = hasPermission(permissions, 'create_system_label_template');

    const handleCreate = async data => {
        const dataToSend = {...data};
        if (!dataToSend[System]) {
            dataToSend["CustomerID"] = activeCustomerID;
        } else {
            dataToSend["CustomerID"] = null;
        }
        if (!hasCreateSystemLabelTemplatePermission) {
            delete dataToSend["CustomerID"];
        }
        delete dataToSend[System];
        const result = await createLabelTemplate({...dataToSend, ActiveCustomerID: activeCustomerID });
        if (result) {
            History.goBack();
        }
    };

    const handleEdit = async data => {
        const dataToSend = {...data};
        delete dataToSend[System];
        const result = await editLabelTemplate({...dataToSend, LabelTemplateID: labelTemplate?.LabelTemplateID, ActiveCustomerID: activeCustomerID });
        if (result) {
            History.goBack();
        }
    };

    const initialValues = {
        [Width]: labelTemplate?.[Width] ?? null,
        [Height]: labelTemplate?.[Height] ?? null,
        [Data]: labelTemplate?.[Data] ?? "",
        [ProductType]:  labelTemplate?.[ProductType] ?? "PCProduct",
        [Format]: labelTemplate?.[Format] ?? formats[0].value,
        [System]: isEdit? null : !!hasCreateSystemLabelTemplatePermission,
        [Default]: labelTemplate?.[Default] ?? false,
        [Name]: labelTemplate?.[Name] ?? "",
        [Purpose]: labelTemplate?.[Purpose] ?? "erasure",
    }

    const requiredText = translations["this_field_is_required" || "This field is required"];

    const formSettings = {
        Validation: {
            [Width]: Yup.number().min(0.001).max(15.0).required(requiredText),
            [Height]: Yup.number().min(0.001).max(15.0).required(requiredText),
            [Data]: Yup.string().required(requiredText),
            [ProductType]: Yup.string().required(requiredText),
            [Format]: Yup.string().required(requiredText),
            [Purpose]: Yup.string().required(requiredText),
        },
        FormTitle: isEdit? translations['edit_label_template'] || 'Edit label template' : translations['create_label_template'] || 'Create label template',
        ButtonSubmit: hasPermission(permissions, isEdit? 'label_template_save' : 'label_template_create') && {
            Title: translations['save'] || 'SAVE',
            Color: 'primary'
        },
    };

    const loadingScreen = <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={loading} PaperProps={{classes: {root: classes.loadingScreen}}}>
        <AppLogoLoading />
    </Dialog>

    return <>
        {loading? loadingScreen : <LabelTemplateForm
            initialValues={initialValues}
            formSettings={formSettings}
            handleSubmit={isEdit ? handleEdit : handleCreate}
            translations={translations}
            permissions={permissions}
            activeCustomerID={activeCustomerID}
            isEdit={isEdit}
            labelTemplate={labelTemplate}
        />}
    </>
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
    };
};
const mapDispatchToProps = dispatch => {
    return {
        fetchLabelTemplate: id => dispatch(labelTemplateActions.fetchLabelTemplate(id)),
        createLabelTemplate: data => dispatch(labelTemplateActions.createLabelTemplate(data)),
        editLabelTemplate: data => dispatch(labelTemplateActions.editLabelTemplate(data))
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(LabelTemplateDetails));
